<template>
  <div class="expense-page">
    <div class="page-header">
      <div>
        <h1>Expense Track - Reports</h1>
        <p>Category-wise and monthly report views</p>
      </div>
      <button class="mini disabled" disabled>Export CSV (coming soon)</button>
    </div>

    <div class="tabs">
      <button :class="['tab', { active: activeTab === 'category' }]" @click="switchTab('category')">Category-wise</button>
      <button :class="['tab', { active: activeTab === 'monthly' }]" @click="switchTab('monthly')">Monthly</button>
    </div>

    <div v-if="activeTab === 'category'" class="panel">
      <div class="filters">
        <input class="input" type="date" v-model="categoryFilters.fromdate" />
        <input class="input" type="date" v-model="categoryFilters.todate" />
        <button class="btn btn-primary" :disabled="loading" @click="fetchCategoryWise">Load</button>
      </div>
      <div v-if="loading" class="state">Loading report...</div>
      <div v-else-if="categoryRows.length === 0" class="state">No category-wise data found.</div>
      <div v-else class="table-wrap">
        <table class="table">
          <thead>
            <tr><th>Type</th><th>Category</th><th>Txn Count</th><th>Total Amount</th></tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in categoryRows" :key="`${row.type}-${row.categoryname}-${idx}`">
              <td>{{ row.type || '--' }}</td>
              <td>{{ row.categoryname || '--' }}</td>
              <td>{{ row.txncount ?? 0 }}</td>
              <td>{{ fmt(row.totalamount) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-else class="panel">
      <div class="filters">
        <input class="input" type="number" min="2000" max="2100" v-model.number="monthlyFilters.year" />
        <button class="btn btn-primary" :disabled="loading" @click="fetchMonthly">Load</button>
      </div>
      <div v-if="loading" class="state">Loading report...</div>
      <div v-else-if="monthlyRows.length === 0" class="state">No monthly data found.</div>
      <div v-else class="table-wrap">
        <table class="table">
          <thead>
            <tr><th>Month</th><th>Income</th><th>Expense</th><th>Rent Income</th><th>Balance</th></tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in monthlyRows" :key="`${row.month}-${idx}`">
              <td>{{ row.month || '--' }}</td>
              <td>{{ fmt(row.income) }}</td>
              <td>{{ fmt(row.expense) }}</td>
              <td>{{ fmt(row.rentincome || row.rentIncome) }}</td>
              <td>{{ fmt(row.balance) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="toast.message" :class="['toast', toast.type]">{{ toast.message }}</div>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '@/utilities/errorHelper';

export default {
  name: 'ExpenseReportsView',
  data() {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const startOfMonth = `${yyyy}-${mm}-01`;
    const todayIso = `${yyyy}-${mm}-${dd}`;
    return {
      activeTab: 'category',
      loading: false,
      categoryRows: [],
      monthlyRows: [],
      categoryFilters: { fromdate: startOfMonth, todate: todayIso },
      monthlyFilters: { year: new Date().getFullYear() },
      toast: { message: '', type: 'success' },
    };
  },
  computed: {
    objectId() {
      return this.$route.params.id || this.$route.query.objectid || '';
    },
  },
  mounted() {
    this.fetchCategoryWise();
  },
  methods: {
    fmt(v) {
      const n = Number(v || 0);
      return new Intl.NumberFormat('en-NP', { style: 'currency', currency: 'NPR', maximumFractionDigits: 2 }).format(n);
    },
    showToast(message, type = 'success') {
      this.toast = { message, type };
      window.setTimeout(() => {
        if (this.toast.message === message) this.toast.message = '';
      }, 3000);
    },
    switchTab(tab) {
      this.activeTab = tab;
      if (tab === 'monthly') {
        this.fetchMonthly();
      } else {
        this.fetchCategoryWise();
      }
    },
    async fetchCategoryWise() {
      if (!this.objectId || !this.categoryFilters.fromdate || !this.categoryFilters.todate) return;
      this.loading = true;
      try {
        const response = await apiService.getExpenseCategoryWiseReport(this.objectId, {
          fromdate: this.categoryFilters.fromdate,
          todate: this.categoryFilters.todate,
        });
        const data = response?.data?.data;
        this.categoryRows = Array.isArray(data) ? data : Array.isArray(response?.data) ? response.data : [];
      } catch (error) {
        if (isNoDataError(error)) {
          this.categoryRows = [];
        } else if (!isHmsSubscriptionInactiveError(error)) {
          this.showToast(error?.response?.data?.reason || 'Failed to load category-wise report', 'error');
        }
      } finally {
        this.loading = false;
      }
    },
    async fetchMonthly() {
      if (!this.objectId || !this.monthlyFilters.year) return;
      this.loading = true;
      try {
        const response = await apiService.getExpenseMonthlyReport(this.objectId, {
          year: this.monthlyFilters.year,
        });
        const data = response?.data?.data;
        this.monthlyRows = Array.isArray(data) ? data : Array.isArray(response?.data) ? response.data : [];
      } catch (error) {
        if (isNoDataError(error)) {
          this.monthlyRows = [];
        } else if (!isHmsSubscriptionInactiveError(error)) {
          this.showToast(error?.response?.data?.reason || 'Failed to load monthly report', 'error');
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.expense-page { padding: 20px; background: #f8fafc; min-height: 100%; }
.page-header { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 12px; }
.page-header h1 { margin: 0; font-size: 20px; color: #1e293b; }
.page-header p { margin: 0; color: #64748b; font-size: 13px; }
.tabs { display: flex; gap: 8px; margin-bottom: 10px; }
.tab { border: 1px solid #d8dbe2; background: #fff; border-radius: 8px; padding: 8px 12px; cursor: pointer; }
.tab.active { color: #fff; background: linear-gradient(135deg, #5b2e8e, #7c3aed); border-color: #5b2e8e; }
.panel { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; }
.filters { display: flex; gap: 8px; margin-bottom: 10px; }
.input { padding: 10px 12px; border: 1px solid #e2e8f0; border-radius: 8px; background: #fff; }
.btn { border: 0; border-radius: 8px; padding: 10px 14px; font-weight: 600; cursor: pointer; }
.btn-primary { background: linear-gradient(135deg, #5b2e8e, #7c3aed); color: #fff; }
.mini { border: 1px solid #e2e8f0; background: #fff; border-radius: 8px; padding: 8px 12px; }
.disabled { opacity: 0.65; cursor: not-allowed; }
.table-wrap { overflow: auto; }
.table { width: 100%; border-collapse: collapse; }
.table th, .table td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; text-align: left; font-size: 13px; white-space: nowrap; }
.table th { background: #f1f5f9; color: #475569; font-weight: 600; }
.state { border: 1px dashed #e2e8f0; border-radius: 8px; padding: 18px; color: #64748b; }
.toast { position: fixed; right: 16px; top: 84px; padding: 10px 12px; border-radius: 8px; color: #fff; z-index: 1300; }
.toast.success { background: #16a34a; }
.toast.error { background: #dc2626; }
</style>
